import { useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useFetch } from '../../hooks/useFetch'
import PersonRecord from '../organisms/PersonRecord'
import { DataContext } from '../../contexts/dataContext'
import { lang } from '../../utils/lang'
import { BASE_URL } from '../../utils/constants'

const PersonFetcher = ({
  personId,
  handleId,
  nonFatalCase,
  handleNonFatalCase,
  allRecords
}) => {
  const { ui, formattedData, formattedNonFatalData } = useContext(DataContext)
  const language = ui === 'fa' ? 'fa/' : ''
  //for search, see if person is in current mapped dataset
  const person = nonFatalCase
    ? formattedNonFatalData.find((d) => d.id === personId)
    : formattedData.find((d) => d.id === personId)

  //get all new data if don't have person in current dataset (due to being filtered out) else get only data fields I don't have yet
  const query = !person
    ? `${BASE_URL}/${language}omiddata/getperson?id=${personId}&fields=fnames,sname,age,viol_startdate,viol_id,viol_full_placename,viol_coordinates,photo_id,was_arrest,was_prosecution,non_fatal,nati,reli,public_remarks,media_urls,library_items`
    : `${BASE_URL}/${language}omiddata/getperson?id=${personId}&fields=nati,reli,public_remarks,media_urls,library_items,non_fatal,viol_id`

  const { data, loading, error } = useFetch(query)
  //handles situation of search: how to know if case is non fatal if not coming from map where we can look at data before rendering the modal
  useEffect(() => {
    if (!person && data && data[personId].viol_id === null) {
      handleNonFatalCase(true)
    }
  }, [data, person, handleNonFatalCase, personId])

  if (loading) return <p>{lang[ui]['Loading']}</p>
  if (error) return <p> {lang[ui]['Error']}</p>

  const process = (data) => {
    //commenting out data not needed for modal:
    const value = data[personId]
    return {
      id: +personId,
      fnames: value.fnames,
      sname: value.sname,
      age: value.age,
      //if not killed, there is no viol_id so take location from first threat
      location: value.viol_id
        ? value.viol_full_placename
        : value.non_fatal[0].viol_loca,
      date: value.viol_startdate ? value.viol_startdate : null,
      year: value.viol_startdate ? value.viol_startdate.slice(0, 4) : null,
      //coordinates: value.viol_coordinates ? value.viol_coordinates : null,
      nati: value.nati,
      reli: value.reli,
      public_remarks: value.public_remarks,
      photoUrlLg: value.photo_id
        ? `${BASE_URL}/actorphotos/${value.photo_id}.png`
        : null,
      photoUrlSm: value.photo_id
        ? `${BASE_URL}/actorphotos/${value.photo_id}_thumb.jpeg `
        : null,
      //arrested: +value.was_arrest,
      //prosecuted: +value.was_prosecution
      //TODO: Check this when have cases that are non_fatal but not later killed. Data here not coming from killed array or threat array, so have to determine if they were ultimately killed or not.
      isNonFatal: value.viol_id ? false : true,
      non_fatal: value.non_fatal
    }
  }
  const dataToUse = !person ? process(data) : data[personId]

  //1. not a fatal case -- only threats -- may be 1 or more than 1
  //2. a fatal case -- maybe no threats -- so only 1
  //3. a fatal case -- with threats -- more than 1
  const findThreatDates = () => {
    let sortedRecordArrayByDate
    if (allRecords && allRecords.length > 1) {
      sortedRecordArrayByDate = allRecords.sort((a, b) =>
        +a.properties.date > +b.properties.date ? 1 : -1
      )
    }
    if (allRecords && allRecords.length === 1) {
      sortedRecordArrayByDate = allRecords
    }

    let threatDates = []
    if (sortedRecordArrayByDate) {
      sortedRecordArrayByDate.forEach((record) =>
        record.properties.nonFatal
          ? threatDates.push(record.properties.date)
          : null
      )
      //need to go back here to original data from query instead of the parsed data I already have in order to get all threats for one person
    } else if (dataToUse.non_fatal) {
      const sorted = dataToUse.non_fatal.sort((a, b) =>
        +a.viol_startdate > +b.viol_startdate ? 1 : -1
      )
      sorted.forEach((record) => threatDates.push(record.viol_startdate))
    }
    return threatDates.length ? threatDates : null
  }
  const threatDates = findThreatDates()
  return (
    <PersonRecord
      person={person ? person : dataToUse}
      newData={dataToUse}
      threatDates={threatDates}
      personId={personId}
      handleId={handleId}
      //person is not defined if we are searching someone who is filtered out.
      //nonFatalCase is null when coming from search
      //viol_id is null when the person has not been killed
      nonFatalCase={
        !person
          ? dataToUse.isNonFatal
          : nonFatalCase !== null
          ? nonFatalCase
          : dataToUse.viol_id === null
          ? true
          : null
      }
    />
  )
}
export default PersonFetcher

PersonFetcher.propTypes = {
  personId: PropTypes.number.isRequired,
  handleId: PropTypes.func
}
